import { osnovaApi } from '../base';
import type { AdminAnnouncement, Announcement, AnnouncementKind, GenBatchStatus, GenerateAnnouncementsBody, PrCandidate, PublishedPr } from '@entities/announcement';

export const announcementsApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Лента опубликованных обновлений для текущего пользователя. Бэкенд
     *  отдаёт `{ items }`; распрямляем в массив. Бейдж непрочитанных считается
     *  из `read_at == null`. WS-событие `announcement_published` инвалидирует. */
    listAnnouncements: b.query<Announcement[], void>({
      query: () => '/announcements',
      transformResponse: (res: { items: Announcement[] }) => res.items,
      providesTags: ['Announcement'],
    }),
    /** Пометить одну карточку прочитанной (204). */
    markAnnouncementRead: b.mutation<void, string>({
      query: (id) => ({ url: `/announcements/${id}/read`, method: 'POST' }),
      invalidatesTags: ['Announcement'],
    }),
    /** Пометить все карточки прочитанными (204). */
    markAllAnnouncementsRead: b.mutation<void, void>({
      query: () => ({ url: '/announcements/read-all', method: 'POST' }),
      invalidatesTags: ['Announcement'],
    }),
    /** Пометить всплывающую модалку анонса показанной этому пользователю (204,
     *  идемпотентно). Вызывается в момент ПОКАЗА — после этого карточка
     *  приходит с непустым `popup_seen_at` и в очередь всплывашек не попадает
     *  (гарантия «показать 1 раз»).
     *
     *  Оптимистично проставляем `popup_seen_at` прямо в кэше ленты, БЕЗ
     *  инвалидации тега: иначе рефетч мог бы на миг вернуть карточку ещё
     *  непоказанной и `UpdatesPopup` переотобрал бы её (мигание). Сервер —
     *  источник правды, при следующем полном рефетче ленты значение совпадёт. */
    markAnnouncementSeen: b.mutation<void, string>({
      query: (id) => ({ url: `/announcements/${id}/seen`, method: 'POST' }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          announcementsApi.util.updateQueryData('listAnnouncements', undefined, (draft) => {
            const item = draft.find((a) => a.id === id);
            if (item && item.popup_seen_at == null) {
              item.popup_seen_at = new Date().toISOString();
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    /** Убрать одну карточку из своей ленты (204). */
    clearAnnouncement: b.mutation<void, string>({
      query: (id) => ({ url: `/announcements/${id}/clear`, method: 'POST' }),
      invalidatesTags: ['Announcement'],
    }),
    /** Очистить всю свою ленту обновлений (204). */
    clearAllAnnouncements: b.mutation<void, void>({
      query: () => ({ url: '/announcements/clear-all', method: 'POST' }),
      invalidatesTags: ['Announcement'],
    }),

    /* ── Админ-ревью черновиков обновлений (гейт публикации) ──────────────── */

    /** Список карточек для админ-ревью (черновики + опубликованные). */
    listAdminAnnouncements: b.query<AdminAnnouncement[], void>({
      query: () => '/admin/announcements',
      transformResponse: (res: { items: AdminAnnouncement[] }) => res.items,
      providesTags: ['AdminAnnouncement'],
    }),
    /** Создать карточку-черновик вручную. Идемпотентность по `source_ref` —
     *  `created=false` означает, что карточка уже была. */
    createAnnouncement: b.mutation<
      { announcement: AdminAnnouncement; created: boolean },
      {
        agent_code?: string | null;
        kind: AnnouncementKind;
        title: string;
        body: string;
        source_ref?: string | null;
      }
    >({
      query: (body) => ({ url: '/admin/announcements', method: 'POST', body }),
      invalidatesTags: ['AdminAnnouncement', 'Announcement'],
    }),
    /** Частично обновить черновик (title/body/kind/agent_code). */
    updateAnnouncement: b.mutation<
      { announcement: AdminAnnouncement },
      {
        id: string;
        patch: Partial<{
          agent_code: string | null;
          kind: AnnouncementKind;
          title: string;
          body: string;
        }>;
      }
    >({
      query: ({ id, patch }) => ({ url: `/admin/announcements/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: ['AdminAnnouncement', 'Announcement'],
    }),
    /** Опубликовать черновик — разослать пользователям. После публикации PR
     *  карточки попадает в список «Опубликованные PR» → инвалидируем `PublishedPr`. */
    publishAnnouncement: b.mutation<{ announcement: AdminAnnouncement }, string>({
      query: (id) => ({ url: `/admin/announcements/${id}/publish`, method: 'POST' }),
      invalidatesTags: ['AdminAnnouncement', 'Announcement', 'PublishedPr'],
    }),
    /** Удалить карточку (204). Удаление опубликованной карточки возвращает её PR
     *  в кандидаты и убирает из опубликованных → инвалидируем оба списка. */
    deleteAnnouncement: b.mutation<void, string>({
      query: (id) => ({ url: `/admin/announcements/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdminAnnouncement', 'Announcement', 'PublishedPr', 'PrCandidate'],
    }),

    /* ── Генерация черновиков из смердженных PR (админ) ───────────────────── */

    /** Смердженные PR-кандидаты для генерации карточек. `agent` — фильтр:
     *  `all` (все) / `system` (общесистемные) / конкретный agent_code. */
    getPrCandidates: b.query<PrCandidate[], string>({
      query: (agent) => `/admin/announcements/pr-candidates?agent=${encodeURIComponent(agent)}`,
      transformResponse: (res: { items: PrCandidate[] }) => res.items,
      providesTags: ['PrCandidate'],
    }),
    /** Запустить фоновую генерацию черновиков по выбранным PR (202 → batch_id).
     *  НЕ инвалидирует `Announcement`: черновик не попадает в пользовательскую
     *  ленту до публикации. Свежие черновики подтянет `AdminAnnouncement`. */
    generateAnnouncements: b.mutation<{ batch_id: string }, GenerateAnnouncementsBody>({
      query: (body) => ({ url: '/admin/announcements/generate', method: 'POST', body }),
      invalidatesTags: ['AdminAnnouncement', 'PrCandidate'],
    }),
    /** Статус фоновой джобы генерации (поллинг до терминального статуса). */
    getGenBatchStatus: b.query<GenBatchStatus, string>({
      query: (batchId) => `/admin/announcements/generate/${batchId}`,
    }),
    /** PR, уже вышедшие в опубликованных карточках (отдельный список «Опубликованные PR»). */
    getPublishedPrs: b.query<PublishedPr[], void>({
      query: () => '/admin/announcements/pr-published',
      transformResponse: (res: { items: PublishedPr[] }) => res.items,
      providesTags: ['PublishedPr'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListAnnouncementsQuery,
  useMarkAnnouncementReadMutation,
  useMarkAnnouncementSeenMutation,
  useMarkAllAnnouncementsReadMutation,
  useClearAnnouncementMutation,
  useClearAllAnnouncementsMutation,
  useListAdminAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  usePublishAnnouncementMutation,
  useDeleteAnnouncementMutation,
  useGetPrCandidatesQuery,
  useGenerateAnnouncementsMutation,
  useGetGenBatchStatusQuery,
  useGetPublishedPrsQuery,
} = announcementsApi;
